import React, { useReducer, useRef, useState, useEffect } from 'react';
import { Button, Stack, Fade, Box } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from './API';
import { styles } from './styles';
import Search from '../SmartForms/Search';
import GenericTable from '../Components/GenericTable/GenericTable';
import CandidatesTableRows from '../Candidates/CandidatesTableRows';
import { headerStyle } from '../Candidates/config';
import { CandidatesReducer, InitialCandidatesState } from './reducer';
import { ICandidateSourcingSearch, ICandidateSourcingFilters, IApiResponse } from './types';
import TotalSelectedIndicatorWithActions from '../Components/GenericTable/TotalSelectedIndicatorWithActions';
import DeleteIcon from '@mui/icons-material/Delete';
import { tableSearchButtonStyles, baseSecondaryButtonStyles } from '../shared/commonStyles';
import JobSelectionDialog from './JobSelectionDialog';
import { IJob, FilterKey } from './types';
import FelixAnimation from './FelixAnimation';
import Filters from './Filters';
import FiltersBreakdown from './FiltersBreakdown';

export default function CandidateSourcing({ searchObj }: { searchObj: ICandidateSourcingSearch }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [CandidatesState, dispatch] = useReducer(CandidatesReducer, InitialCandidatesState);
  const {
    candidatesCount,
    totalPages,
    candidatesTableSettings,
    candidatesTableState,
    optionsCleared,
    isJobDialogOpen,
    typesToExpand,
    progress,
    totalSteps,
    currentStep,
    transitioningStep,
    progressVisible,
    fetchingAssessedPercentage,
    showResults
  } = CandidatesState;
  const [searchObject, setSearchObject] = useState(searchObj);
  const [selectedJob, setSelectedJob] = useState<IJob | undefined>(searchObject.job);
  const progressIntervalRef = useRef<NodeJS.Timeout>();

  const filters = (searchObject.filters || {}) as ICandidateSourcingFilters;

  const transitionToStep = (step: string) => {
    dispatch({ type: 'SET_TRANSITIONING_STEP', payload: true });
    setTimeout(() => {
      dispatch({ type: 'SET_CURRENT_STEP', payload: step });
    }, 400);
    setTimeout(() => {
      dispatch({ type: 'SET_TRANSITIONING_STEP', payload: false });
    }, 600);
  };

  const setErrorSnackbar = (message: string) => {
    dispatch({
      type: 'SET_SNACKBAR',
      payload: { message, state: 'error' }
    });
  };

  const { mutate: updateSearch } = useMutation({
    mutationFn: async ({ query, jobId }: { query: string, jobId?: number }) => {
      const response = await Api.updateSearch(searchObj.id, query, jobId);
      setSearchObject(response.res as ICandidateSourcingSearch);
      return response;
    }
  });

  const { mutate: executeSearch, isLoading: isExecutingSearch } = useMutation({
    mutationFn: async () => {
      transitionToStep('Executing search...');
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.executeSearch(searchObj.id, false);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
      dispatch({ type: 'COMPLETE_EXECUTING_SEARCH' });
      startProgressAnimation(100);
    },
    onError() {
      setErrorSnackbar('There was an error executing the search.');
    }
  });

  const { mutate: updateFilters, isLoading: isUpdatingFilters } = useMutation({
    mutationFn: async (newFilters: ICandidateSourcingFilters) => {
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.updateFilters(searchObj.id, newFilters);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error updating the filters.');
    }
  });

  const startProgressAnimation = (targetProgress: number) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    const interval = 200;

    progressIntervalRef.current = setInterval(() => {
      dispatch({ type: 'INCREMENT_PROGRESS', payload: { targetProgress } });
      if (progress >= targetProgress && progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }, interval);
  };

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const { mutate: expandFilters } = useMutation({
    mutationFn: async () => {
      const firstType = typesToExpand[0];
      let label = '';
      let extension = '';
      switch (firstType) {
        case 'job_titles':
          label = 'job titles';
          if (filters.job_titles && filters.job_titles.length === 1) {
            extension = ` to "${filters.job_titles[0]}"`;
          }
          break;
        case 'skills':
          label = 'skills';
          if (filters.skills && filters.skills[0] && filters.skills[0].length === 1) {
            extension = ` to "${filters.skills[0][0]}"`;
          }
          break;
        case 'industries':
          label = 'industries';
          if (filters.industries && filters.industries.length === 1) {
            extension = ` to "${filters.industries[0]}"`;
          }
          break;
      }
      transitionToStep(`Looking up similar ${label}${extension}...`);
      const response = await Api.expandFilters(searchObj.id, firstType);
      if (response.res) {
        setSearchObject(response.res as ICandidateSourcingSearch);
      }
      return response;
    },
    onSuccess: () => {
      const remainingTypes = typesToExpand.slice(1);
      dispatch({ type: 'SET_TYPES_TO_EXPAND', payload: remainingTypes });
      const currentStep = totalSteps - remainingTypes.length - 3;
      const nextStep = currentStep + 1;
      dispatch({ type: 'SET_PROGRESS', payload: (currentStep * 100) / totalSteps });
      startProgressAnimation((nextStep * 100) / totalSteps);
      if (remainingTypes.length === 0) {
        executeSearch();
      }
    },
    onError() {
      setErrorSnackbar('There was an error expanding the filters.');
    }
  });

  const { mutate: removeSelectedCandidates } = useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await Api.bulkRemoveCandidates(searchObj.id, ids);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error deleting the selected candidates.');
    }
  });

  const {
    data: candidates,
    isLoading: isFetchingCandidates,
    refetch: refetchCandidates
  } = useQuery({
    queryKey: [
      'candidates',
      searchObj.id,
      candidatesTableSettings.page,
      candidatesTableSettings.limit,
      candidatesTableSettings.sort,
      candidatesTableSettings.sort_order
    ],
    queryFn: async () => {
      transitionToStep('Reloading candidates...');
      const candidatesResponse = (await Api.fetchCandidates(
        searchObj.id,
        candidatesTableSettings.page,
        candidatesTableSettings.limit,
        candidatesTableSettings.sort,
        candidatesTableSettings.sort_order
      )) as IApiResponse;
      dispatch({
        type: 'SET_CANDIDATES_COUNT',
        payload: parseInt(candidatesResponse.resHead?.['x-total-count'] || '0')
      });
      dispatch({
        type: 'SET_TOTAL_PAGES',
        payload: parseInt(candidatesResponse.resHead?.['x-total-pages'] || '0')
      });
      transitionToStep('Preparing screening conditions...');
      dispatch({ type: 'SET_FETCHING_ASSESSED_PERCENTAGE', payload: true });
      setTimeout(() => {
        refetchAssessedPercentage();
      }, 10000);
      return candidatesResponse.res.candidates || [];
    }
  });

  const { refetch: refetchAssessedPercentage } = useQuery({
    queryKey: ['assessed_percentage', searchObj.id],
    queryFn: async () => {
      const response = await Api.assessedPercentage(searchObj.id);
      return response.res;
    },
    refetchInterval: (data) => {
      if (!fetchingAssessedPercentage || (data && data.assessed_percentage > 80)) {
        return false;
      }
      return 10000;
    },
    onSuccess: (data: { assessed_percentage: number }) => {
      if (!data) return;
      transitionToStep(`Assessing candidates (${Math.round(data.assessed_percentage)}%) ...`);

      if (data.assessed_percentage > 80) {
        dispatch({ type: 'COMPLETE_ASSESSING_CANDIDATES' });
        transitionToStep('');
        setTimeout(() => {
          dispatch({ type: 'SET_PROGRESS', payload: 0 });
        }, 1000);
      }
    },
    refetchIntervalInBackground: true
  });

  const setQuery = (newQuery: string) => {
    updateSearch({
      query: newQuery,
      jobId: selectedJob?.id
    });
    dispatch({ type: 'SET_OPTIONS_CLEARED', payload: true });
  };

  const doSearch = () => {
    const typesToExpand = ['job_titles', 'skills', 'industries'].filter(
      (filter) =>
        filters[filter as FilterKey] && (filters[filter as FilterKey] as string[]).length > 0
    );
    dispatch({ type: 'START_SEARCH', payload: { typesToExpand } });
    startProgressAnimation(100 / (typesToExpand.length + 3));
  };

  const handleJobSelect = (job: IJob | undefined) => {
    setSelectedJob(job);
    updateSearch({
      query: searchObject.query,
      jobId: job?.id
    });
  };

  useEffect(() => {
    if (typesToExpand.length > 0) {
      expandFilters();
    }
  }, [typesToExpand]);

  return (
    <Stack sx={styles.candidatesPageContainer} ref={containerRef}>
      <Stack sx={styles.candidatesPageHeader} direction="row" justifyContent="space-between">
        Candidate Sourcing
        <Button
          onClick={() => dispatch({ type: 'SET_JOB_DIALOG_OPEN', payload: true })}
          sx={baseSecondaryButtonStyles}
        >
          Job Description
        </Button>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Search
          idLabel="candidates"
          placeholder="Enter a query"
          setSearch={(newQuery) => setQuery(newQuery)}
          initialValue={searchObject.query || ''}
        />
        <Button
          onClick={doSearch}
          sx={{ ...tableSearchButtonStyles, marginTop: '-5px !important', padding: '0 20px' }}
          disabled={isExecutingSearch || isUpdatingFilters || isFetchingCandidates}
        >
          Search
        </Button>
      </Stack>
      <FiltersBreakdown
        searchObject={searchObject}
        setSearchObject={setSearchObject}
        optionsCleared={optionsCleared}
      />
      <FelixAnimation
        query={searchObject.query}
        progressVisible={progressVisible}
        transitioningStep={transitioningStep}
        currentStep={currentStep}
        progress={progress}
      />
      <Filters
        searchObject={searchObject}
        setSearchObject={setSearchObject}
        updateFilters={updateFilters}
        optionsCleared={optionsCleared}
        isUpdatingFilters={isUpdatingFilters}
      />
      <Box sx={{ position: 'relative', minHeight: '40px' }}>
        <Fade in={showResults && candidatesCount > 0} timeout={500}>
          <Stack
            sx={{
              position: 'absolute',
              width: '100%',
              top: 0,
              left: 0
            }}
          >
            <em>
              Found <strong className="px-2">{candidatesCount}</strong> candidates
            </em>
            <Button
              onClick={() => {
                location.href = `/admin/candidate_sourcing/searches/${searchObj.id}/screening`;
              }}
              sx={{ ...tableSearchButtonStyles, width: '146px', height: '35px', marginTop: '10px' }}
            >
              Screen Candidates
            </Button>
          </Stack>
        </Fade>
      </Box>
      <GenericTable
        label="candidates list"
        items={candidates || []}
        isLoadingItems={isFetchingCandidates || isExecutingSearch}
        tableState={candidatesTableState}
        dispatch={dispatch}
        headerStyle={headerStyle}
        paginationState={candidatesTableSettings}
        pagination={{
          totalItems: candidatesCount,
          totalPages: totalPages
        }}
        TableRows={CandidatesTableRows}
        TableActionList={undefined}
        allowMultipleSelection={candidatesCount > 0}
        titleCellId="id"
        userPreferenceSource="candidates"
      />
      <TotalSelectedIndicatorWithActions
        totalSelected={candidatesTableState.selectedItemIds.length}
        parentDiv={containerRef.current}
        dispatch={dispatch}
        actionButtonId="delete-candidate-action-button"
        actionButtonsList={[
          {
            id: 'delete',
            label: 'Delete',
            icon: <DeleteIcon />
          }
        ]}
        actionCall={() => {
          removeSelectedCandidates(candidatesTableState.selectedItemIds);
          dispatch({
            type: 'REMOVE_ALL_PAGE_ITEMS',
            payload: candidatesTableState.selectedItemIds
          });
        }}
      />
      <JobSelectionDialog
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        isOpen={isJobDialogOpen}
        onClose={() => dispatch({ type: 'SET_JOB_DIALOG_OPEN', payload: false })}
        onUpdate={handleJobSelect}
      />
    </Stack>
  );
}
